import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import {
  DEFAULT_STATUS_OPTIONS,
  STAGE_KINDS,
  STAGE_POPULATIONS,
  STAGE_PUBLICATIONS,
  type StageInfo,
  type StageStatusOption,
} from '@concours/shared'
import { Alert, Button, Input, Modal, Select, Textarea, useAuth, useToast } from '@concours/ui'
import { errorMessage, fieldErrors } from '../../lib/errors'
import { SELECTION_KEY, fromLocalInput, toLocalInput } from '../../lib/selection'

const options = (o: Record<string, string>) =>
  Object.entries(o).map(([value, label]) => ({ value, label }))

/** Création / modification d'une étape (cahier §9.2). */
export function StageModal({
  competitionId,
  stage,
  first,
  onClose,
}: {
  competitionId: string
  stage: StageInfo | null
  /** Première étape du parcours : population = dossiers recevables. */
  first: boolean
  onClose: () => void
}) {
  const { session } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const locked = Boolean(stage && stage.status !== 'draft')
  const [form, setForm] = useState({
    label: stage?.label ?? '',
    kind: stage?.kind ?? 'written',
    graded: stage?.graded ?? true,
    population: stage?.population ?? (first ? 'admissible' : 'previous'),
    convocationAt: toLocalInput(stage?.convocationAt ?? null),
    startsAt: toLocalInput(stage?.startsAt ?? null),
    endsAt: toLocalInput(stage?.endsAt ?? null),
    seats: stage?.seats ? String(stage.seats) : '',
    publication: stage?.publication ?? 'after_approval',
    commissionValidation: stage?.commissionValidation ?? true,
    convocationTemplate: stage?.convocationTemplate ?? '',
  })
  const [statuses, setStatuses] = useState<StageStatusOption[]>(
    stage?.statuses.length ? stage.statuses : [...DEFAULT_STATUS_OPTIONS]
  )
  const set = (patch: Partial<typeof form>) => setForm((f) => ({ ...f, ...patch }))
  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: form.label.trim(),
        ...(locked
          ? {}
          : {
              kind: form.kind,
              graded: form.graded,
              population: form.population,
              statuses: form.graded ? [] : statuses,
            }),
        convocationAt: fromLocalInput(form.convocationAt),
        startsAt: fromLocalInput(form.startsAt),
        endsAt: fromLocalInput(form.endsAt),
        seats: form.seats ? Number(form.seats) : null,
        publication: form.publication,
        commissionValidation: form.commissionValidation,
        convocationTemplate: form.convocationTemplate.trim() || null,
      }
      return stage
        ? session.api.patch(`/admin/stages/${stage.id}`, body)
        : session.api.post(`/admin/competitions/${competitionId}/stages`, body)
    },
    onSuccess: async () => {
      toast.success(stage ? 'Étape enregistrée' : 'Étape ajoutée au parcours')
      await queryClient.invalidateQueries({ queryKey: SELECTION_KEY })
      onClose()
    },
    onError: (error) => {
      if (!Object.keys(fieldErrors(error)).length) toast.error(errorMessage(error))
    },
  })
  const errors = fieldErrors(save.error)

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={stage ? `Étape ${stage.position} — ${stage.label}` : 'Nouvelle étape'}
      description="Les dates, places et modèles restent modifiables. Le type, la notation et la population sont figés une fois la notation ouverte."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Annuler
          </Button>
          <Button
            variant="primary"
            loading={save.isPending}
            disabled={form.label.trim().length < 3}
            onClick={() => save.mutate()}
          >
            Enregistrer
          </Button>
        </>
      }
    >
      <div className="stack" style={{ gap: 'var(--space-4)' }}>
        {locked && (
          <Alert tone="info">
            Notation ouverte : seules les informations pratiques sont modifiables.
          </Alert>
        )}
        <div className="form-grid">
          <Input
            label="Nom de l’étape"
            required
            value={form.label}
            error={errors.label}
            onChange={(e) => set({ label: e.target.value })}
            fieldClassName="span-2"
          />
          <Select
            label="Type d’étape"
            required
            disabled={locked}
            value={form.kind}
            options={options(STAGE_KINDS)}
            onChange={(e) => set({ kind: e.target.value as typeof form.kind })}
          />
          <Select
            label="Population concernée"
            required
            disabled={locked || first}
            value={form.population}
            error={errors.population}
            hint={first ? 'La première étape porte sur les dossiers recevables.' : undefined}
            options={options(STAGE_POPULATIONS)}
            onChange={(e) => set({ population: e.target.value as typeof form.population })}
          />
          <Select
            label="Notation"
            required
            disabled={locked}
            value={form.graded ? 'graded' : 'status'}
            error={errors.graded}
            options={[
              { value: 'graded', label: 'Étape notée (disciplines, barèmes)' },
              { value: 'status', label: 'Étape non notée (statut : apte, présent…)' },
            ]}
            onChange={(e) => set({ graded: e.target.value === 'graded' })}
          />
          <Input
            label="Places / quota"
            type="number"
            min={1}
            value={form.seats}
            error={errors.seats}
            hint="Facultatif : nombre de candidats pouvant être retenus."
            onChange={(e) => set({ seats: e.target.value })}
          />
          <Input
            label="Envoi des convocations"
            type="datetime-local"
            value={form.convocationAt}
            onChange={(e) => set({ convocationAt: e.target.value })}
          />
          <Input
            label="Début de l’épreuve"
            type="datetime-local"
            value={form.startsAt}
            onChange={(e) => set({ startsAt: e.target.value })}
          />
          <Input
            label="Fin de l’épreuve"
            type="datetime-local"
            value={form.endsAt}
            error={errors.endsAt}
            onChange={(e) => set({ endsAt: e.target.value })}
          />
          <Select
            label="Publication des résultats"
            value={form.publication}
            options={options(STAGE_PUBLICATIONS)}
            onChange={(e) => set({ publication: e.target.value as typeof form.publication })}
          />
          <label className="check-row span-2">
            <input
              type="checkbox"
              checked={form.commissionValidation}
              onChange={(e) => set({ commissionValidation: e.target.checked })}
            />
            <span>
              Circuit de validation : relevés validés par le responsable de commission avant calcul
            </span>
          </label>
          <Textarea
            label="Modèle de convocation"
            rows={4}
            fieldClassName="span-2"
            value={form.convocationTemplate}
            hint="Texte repris sur la convocation (lieu, pièces à présenter, tenue…). Variables : {nom}, {matricule}, {date}, {centre}."
            onChange={(e) => set({ convocationTemplate: e.target.value })}
          />
        </div>
        {!form.graded && (
          <StatusEditor
            statuses={statuses}
            disabled={locked}
            onChange={setStatuses}
            error={errors.statuses}
          />
        )}
      </div>
    </Modal>
  )
}

/** Statuts d'une étape non notée : libellé et « permet de poursuivre ». */
export function StatusEditor({
  statuses,
  disabled,
  error,
  onChange,
}: {
  statuses: StageStatusOption[]
  disabled?: boolean
  error?: string
  onChange: (s: StageStatusOption[]) => void
}) {
  const update = (i: number, patch: Partial<StageStatusOption>) =>
    onChange(statuses.map((s, j) => (j === i ? { ...s, ...patch } : s)))
  return (
    <fieldset className="stack" style={{ gap: 'var(--space-2)', border: 0, padding: 0, margin: 0 }}>
      <legend className="field-label">Statuts possibles</legend>
      {error && <Alert tone="danger">{error}</Alert>}
      {statuses.map((s, i) => (
        <div
          key={i}
          className="row"
          style={{ gap: 'var(--space-2)', alignItems: 'end', flexWrap: 'wrap' }}
        >
          <Input
            label="Libellé"
            value={s.label}
            disabled={disabled}
            onChange={(e) =>
              update(i, {
                label: e.target.value,
                code:
                  e.target.value
                    .normalize('NFD')
                    .replace(/[̀-ͯ]/g, '')
                    .toLowerCase()
                    .replace(/[^a-z0-9]+/g, '_')
                    .replace(/^_|_$/g, '')
                    .slice(0, 30) || s.code,
              })
            }
          />
          <label className="check-row">
            <input
              type="checkbox"
              checked={s.passes}
              disabled={disabled}
              onChange={(e) => update(i, { passes: e.target.checked })}
            />
            <span>Permet de poursuivre</span>
          </label>
          <Button
            variant="ghost"
            size="sm"
            iconOnly
            icon={<Trash2 />}
            aria-label={`Retirer le statut ${s.label}`}
            disabled={disabled || statuses.length <= 2}
            onClick={() => onChange(statuses.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div>
        <Button
          size="sm"
          icon={<Plus />}
          disabled={disabled || statuses.length >= 12}
          onClick={() =>
            onChange([
              ...statuses,
              { code: `statut_${statuses.length + 1}`, label: '', passes: false },
            ])
          }
        >
          Ajouter un statut
        </Button>
      </div>
    </fieldset>
  )
}
